import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Input, Select, Skeleton, Space, Statistic,
  Table, Tag, Typography, message,
} from 'antd';
import { DateField, MoneyField, TextField } from '../../ui/fields';
import { Field, FormModal, FormSection, V } from '../../ui/forms';
import { DollarOutlined, SearchOutlined } from '@ant-design/icons';
import type { InvoiceRow, PaymentMethod } from '@velora/contracts';
import { productApi } from '../../api';

/**
 * Accounts desk — Counter.
 * Search a student by name / admission no. → see their open invoices → record
 * a payment against one. Uses the same fees ledger endpoints as the admin
 * Collections page (M-01 monotonic receipt numbers).
 */
export function AccountsCounterPage() {
  const { institution = '' } = useParams<{ institution: string }>();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [payFor, setPayFor] = useState<InvoiceRow | null>(null);

  const students = useQuery({
    queryKey: ['students-search', institution, search],
    queryFn: () => productApi.listStudents(institution, { q: search || undefined }),
    enabled: !!institution && search.length > 0,
  });

  const openInvoices = useQuery({
    queryKey: ['invoices', institution, { status: 'open' }],
    queryFn: () => productApi.listInvoices(institution, { status: 'open' }),
    enabled: !!institution,
  });

  const receiptsToday = useQuery({
    queryKey: ['receipts', institution, today()],
    queryFn: () => productApi.listReceipts(institution, { from: today(), to: today() }),
    enabled: !!institution,
  });

  const filteredOpen = useMemo(() => {
    if (!search) return [];
    const studentIds = new Set(students.data?.rows.map((s) => s.id) ?? []);
    const admissionNumbers = new Set(students.data?.rows.map((s) => s.admission_number) ?? []);
    return (openInvoices.data?.rows ?? []).filter((i) => studentIds.has(i.student_id) || admissionNumbers.has(i.admission_number));
  }, [search, students.data, openInvoices.data]);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>Counter</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          Search a student → open their invoices → record payment. Receipt number auto-issued.
        </Typography.Text>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
        <Card size="small"><Statistic title="Today's collections" value={`₹${paise(receiptsToday.data?.total_paise ?? 0)}`} valueStyle={{ color: '#1c6442' }} /></Card>
        <Card size="small"><Statistic title="Today's receipts" value={receiptsToday.data?.total ?? 0} /></Card>
        <Card size="small"><Statistic title="All open invoices" value={openInvoices.data?.total ?? 0} /></Card>
      </div>

      <Card size="small" title="Find a student">
        <Input
          size="large"
          allowClear
          autoFocus
          placeholder="Name or admission number…"
          prefix={<SearchOutlined style={{ color: '#8a94a6' }} />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Card>

      {search && students.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}

      {search && students.data && students.data.rows.length === 0 && (
        <Empty description={`No student matches "${search}"`} style={{ padding: 20 }} />
      )}

      {search && students.data && students.data.rows.length > 0 && (
        <Card size="small" title={`${filteredOpen.length} open invoice${filteredOpen.length === 1 ? '' : 's'}`}>
          {filteredOpen.length === 0 ? (
            <Empty description="No open invoices for this student. Nothing to collect." style={{ padding: 20 }} />
          ) : (
            <Table<InvoiceRow>
              size="small"
              pagination={false}
              rowKey="id"
              dataSource={filteredOpen}
              columns={[
                { title: 'Student', key: 's', render: (_, r) => <><b>{r.student_name}</b> <Typography.Text type="secondary" style={{ fontSize: 12 }}>· {r.admission_number} · {r.grade}{r.section ? `-${r.section}` : ''}</Typography.Text></> },
                { title: 'Head', dataIndex: 'fee_head_name', key: 'h' },
                { title: 'Owed', key: 'o', align: 'right', render: (_, r) => <b>₹{paise(r.amount_paise - r.paid_paise)}</b> },
                { title: 'Due', dataIndex: 'due_on', key: 'due', render: (v: string) => v },
                { title: 'Overdue', dataIndex: 'days_overdue', key: 'd', render: (v: number) => v > 0 ? <Tag color="error">{v}d</Tag> : <Tag color="success">on time</Tag> },
                {
                  key: 'a', width: 130, align: 'right',
                  render: (_, r) => <Button size="small" type="primary" icon={<DollarOutlined />} onClick={() => setPayFor(r)}>Pay</Button>,
                },
              ]}
            />
          )}
        </Card>
      )}

      <RecordReceiptModal
        invoice={payFor}
        institution={institution}
        onClose={() => setPayFor(null)}
        onDone={() => {
          setPayFor(null);
          void qc.invalidateQueries({ queryKey: ['receipts', institution] });
          void qc.invalidateQueries({ queryKey: ['invoices', institution] });
          void message.success('Receipt recorded');
        }}
      />
    </Space>
  );
}

function RecordReceiptModal({
  invoice, institution, onClose, onDone,
}: { invoice: InvoiceRow | null; institution: string; onClose: () => void; onDone: () => void }) {
  const owed = invoice ? invoice.amount_paise - invoice.paid_paise : 0;
  return (
    <FormModal<{ amount_rupees: number; method: PaymentMethod; received_on: string; note?: string }>
      open={!!invoice} onClose={onClose}
      title="Record receipt" submitText="Record"
      initial={{ amount_rupees: owed / 100, method: 'upi', received_on: today() }}
      mutation={(v) => productApi.createReceipt(institution, invoice!.id, {
        amount_paise: Math.round(v.amount_rupees * 100),
        method: v.method, received_on: v.received_on,
        note: v.note?.trim() || undefined,
      })}
      onSuccess={onDone}
    >
      {invoice && (
        <div style={{ marginBottom: 12, padding: 10, background: '#f7f7f5', borderRadius: 6 }}>
          <div style={{ fontWeight: 600 }}>{invoice.student_name} · {invoice.admission_number}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
            {invoice.fee_head_name} · owed <b>₹{paise(owed)}</b> · due {invoice.due_on}
          </Typography.Text>
        </div>
      )}
      <FormSection cols={2}>
        <Field name="amount_rupees" label="Amount" required rules={[V.money(0.01, owed / 100)]}>
          <MoneyField min={0.01} max={owed / 100} />
        </Field>
        <Field name="method" label="Method" required>
          <Select options={[
            { value: 'upi', label: 'UPI' }, { value: 'cash', label: 'Cash' },
            { value: 'card', label: 'Card' }, { value: 'bank_transfer', label: 'Bank transfer' },
            { value: 'cheque', label: 'Cheque' },
          ]} />
        </Field>
        <Field name="received_on" label="Received on" required>
          <DateField />
        </Field>
        <Field name="note" label="Note">
          <TextField placeholder="UPI ref, cheque no." />
        </Field>
      </FormSection>
    </FormModal>
  );
}

function today(): string { return new Date().toISOString().slice(0, 10); }
function paise(n: number): string { return (n / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 }); }
